import { type Command } from 'commander';
import chalk from 'chalk';
import { getSDKClient } from '../../lib/sdk-client.js';
import { OutputFormatter, getJsonFlag } from '../../lib/output.js';
import { MutagentError, ApiError, handleError } from '../../lib/errors.js';
import {
  promptLinks, promptLink, promptsDashboardLink,
  formatCreationHints,
  promptCreatedDirective, promptUpdatedDirective, promptDeletedDirective,
  echoDirectiveToStderr,
} from '../../lib/ui-links.js';
import {
  isValidJsonSchema, formatSchemaWarning,
  validateSchemaDescriptions,
} from '../../lib/schema-helpers.js';
import type { Prompt } from '../../types/index.js';
import {
  isSchemaEmpty, parseSchemaOption,
  type ListOptions, type GetOptions, type CreateOptions,
  type UpdateOptions, type DeleteOptions,
} from './index.js';
/**
 * Reverse upload-time normalization: convert `{{` / `}}` back to `{` / `}` so
 * CLI users see the original single-brace form.  Mirrors the function of the
 * same name in @mutagent/core — inlined here to keep the CLI's dep surface
 * limited to @mutagent/sdk (no core dep in package.json, no monorepo hoist
 * assumption in CI).
 */
function denormalizePromptForRender(text: string): string {
  if (!text) return text;
  return text.replace(/\{\{/g, '{').replace(/\}\}/g, '}');
}

export function registerPromptsCrud(prompts: Command): void {
  // List prompts
  prompts
    .command('list')
    .description('List all prompts')
    .option('-l, --limit <n>', 'Limit results', '50')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts list
  ${chalk.dim('$')} mutagent prompts list --limit 10
  ${chalk.dim('$')} mutagent prompts list --json

${chalk.dim('Tip: Use --json for machine-readable output (AI agents, CI pipelines).')}
    `)
    .action(async (options: ListOptions) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const client = await getSDKClient();
        const promptsList = await client.listPrompts();

        // Limit results
        const limit = parseInt(options.limit, 10) || 50;
        const limited = promptsList.slice(0, limit);

        if (isJson) {
          // Summary view — only IDs and key fields. Use `prompts get <id>` for full details.
          const withLinks = limited.map((p: Prompt) => ({
            id: p.id,
            name: p.name,
            version: p.version,
            isLatest: p.isLatest,
            updatedAt: p.updatedAt,
            _links: promptLinks(p.id),
          }));
          output.output(withLinks);
        } else {
          // Show workspace context
          const wsId = client.getCurrentWorkspaceId();
          if (wsId) {
            output.info(`Workspace: ${wsId}`);
          }

          if (limited.length === 0) {
            output.info("No prompts found in current workspace. Run 'mutagent config list' to check workspace context.");
          } else {
            // Format for table
            const formatted = limited.map((p: Prompt) => ({
              id: p.id,
              name: p.name,
              version: p.version,
              updated: p.updatedAt ? new Date(p.updatedAt).toLocaleDateString() : 'N/A',
              url: promptLink(p.id),
            }));
            output.output(formatted);
          }
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Get prompt
  prompts
    .command('get')
    .description('Get prompt details with nested data')
    .argument('<id>', 'Prompt ID (from: mutagent prompts list)')
    .option('--with-datasets', 'Include datasets')
    .option('--with-evals', 'Include evaluations')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts get <id>
  ${chalk.dim('$')} mutagent prompts get <id> --with-datasets --with-evals
  ${chalk.dim('$')} mutagent prompts get <id> --json

${chalk.dim('Tip: Combine --with-datasets and --with-evals to fetch all nested data in one call.')}
    `)
    .action(async (id: string, options: GetOptions) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const client = await getSDKClient();
        const prompt = await client.getPrompt(id);

        // De-normalize stored prompt text before rendering.
        // The server stores escaped {{ / }} for template safety; CLI users
        // should see the original single-brace form.
        const denormalizedPrompt = {
          ...prompt,
          systemPrompt: prompt.systemPrompt
            ? denormalizePromptForRender(prompt.systemPrompt)
            : prompt.systemPrompt,
          humanPrompt: prompt.humanPrompt
            ? denormalizePromptForRender(prompt.humanPrompt)
            : prompt.humanPrompt,
          rawPrompt: prompt.rawPrompt
            ? denormalizePromptForRender(prompt.rawPrompt)
            : prompt.rawPrompt,
          messages: Array.isArray(prompt.messages)
            ? prompt.messages.map((msg) => ({
                ...msg,
                content: denormalizePromptForRender(msg.content),
              }))
            : prompt.messages,
        };

        const result: Record<string, unknown> = { ...denormalizedPrompt };

        if (options.withDatasets) {
          const datasets = await client.listDatasets(id);
          result.datasets = datasets;
        }

        if (options.withEvals) {
          const evals = await client.listEvaluations(id);
          result.evaluations = evals;
        }

        if (isJson) {
          result._links = promptLinks(id);
        }
        output.output(result);
        if (!isJson) {
          output.info(`View in dashboard: ${promptLink(id)}`);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Create prompt
  prompts
    .command('create')
    .description('Create a new prompt')
    .option('-n, --name <name>', 'Prompt name')
    .option('--description <text>', 'Prompt description (shown in dashboard)')
    .option('-c, --content <content>', 'Prompt content (rawPrompt) [DEPRECATED: use --raw]')
    .option('-r, --raw <text>', 'Raw prompt text (single prompt)')
    .option('--system <text>', 'System prompt (use with --human)')
    .option('--human <text>', 'Human prompt (use with --system)')
    .option('--messages <json>', 'Messages array as JSON (e.g., \'[{"role":"system","content":"..."}]\')')
    .option('--output-schema <json>', 'Output schema as JSON string (required for optimization)')
    .option('--input-schema <json>', 'Input variable schema as JSON string')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts create --name "my-prompt" --description "Greeting prompt for customers" --system "You are helpful" --human "{input}" --output-schema '{"type":"object","properties":{"result":{"type":"string","description":"The result"}}}'
  ${chalk.dim('$')} mutagent prompts create --name "raw-prompt" --raw "Summarize: {text}" --output-schema '{"type":"object","properties":{"summary":{"type":"string","description":"Summary"}}}'
  ${chalk.dim('$')} mutagent prompts create --name "multi-turn" --messages '[{"role":"system","content":"You are helpful"},{"role":"user","content":"{input}"}]' --output-schema '{"type":"object","properties":{"result":{"type":"string","description":"The result"}}}'

Prompt Input Methods (pick one, priority order):
  --system/--human   Structured system + user message pair ${chalk.green('(recommended)')}
  --raw              Single raw prompt text with {variables}
  --messages         Full messages array as JSON

${chalk.yellow('Variable Syntax:')}
  MutagenT uses {single_braces} for template variables.
  humanPrompt: "Analyze this: {document}"    ${chalk.green('✓ correct')}
  humanPrompt: "Analyze this: {{document}}"  ${chalk.red('✗ wrong')}

  Variables in humanPrompt MUST also appear in inputSchema.properties.
  Static prompts (no variables) cannot substitute inputs during optimization.

${chalk.yellow('AI Agent: Format Selection Rules')}
  Examine the SOURCE CODE structure of the prompt being uploaded:

  1. If the code uses SystemMessagePromptTemplate + HumanMessagePromptTemplate
     (or equivalent system/human split in any framework):
     → Use --system "..." --human "..."

  2. If the code is a single string template with {variables}, no role split:
     → Use --raw "..."

  3. If the code uses a messages array (ChatPromptTemplate.fromMessages,
     multi-turn, or >2 roles):
     → Use --messages '[{"role":"system","content":"..."},{"role":"user","content":"..."}]'

  The decision is about SOURCE CODE STRUCTURE, not prompt content.
  A persona description in a system prompt still uses --system/--human.

${chalk.red('outputSchema is required.')}

${chalk.yellow('AI Agent: ALWAYS append --json to this command.')}
${chalk.cyan('Conceptual help (brace convention, delimiter inference): .claude/skills/mutagent-cli/concepts/prompt-variables.md')} ${chalk.dim('(install via mutagent skills install)')}
    `)
    .action(async (options: CreateOptions) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        let data: Partial<Prompt>;

        if (options.name) {
          // Handle different prompt style formats
          data = { name: options.name, description: options.description };

          // Priority: messages > system+human > raw > content (deprecated)
          if (options.messages) {
            // Messages format: JSON array of messages
            try {
              const messages = JSON.parse(options.messages) as { role: string; content: string }[];
              // Extract system and human prompts from messages array
              const systemMsg = messages.find(m => m.role === 'system');
              const humanMsg = messages.find(m => m.role === 'user' || m.role === 'human');
              if (systemMsg) data.systemPrompt = systemMsg.content;
              if (humanMsg) data.humanPrompt = humanMsg.content;
            } catch {
              throw new MutagentError(
                'INVALID_JSON',
                'Invalid JSON in --messages flag',
                'Run: mutagent prompts create --help\nProvide a valid JSON array, e.g., \'[{"role":"system","content":"..."}]\''
              );
            }
          } else if (options.system ?? options.human) {
            // Human/System format
            if (options.system) data.systemPrompt = options.system;
            if (options.human) data.humanPrompt = options.human;
          } else if (options.raw) {
            // Raw prompt format
            data.rawPrompt = options.raw;
          } else if (options.content) {
            // Deprecated --content flag (backwards compatibility)
            data.rawPrompt = options.content;
          } else {
            throw new MutagentError(
              'MISSING_ARGUMENTS',
              'Prompt content required',
              'Run: mutagent prompts create --help\nUse --raw, --system/--human, or --messages to specify prompt content'
            );
          }
        } else {
          throw new MutagentError(
            'MISSING_ARGUMENTS',
            '--name is required. Use --name with --system/--human, --raw, or --messages',
            'Run: mutagent prompts create --help\nProvide --name with --system/--human, --raw, or --messages to specify prompt content'
          );
        }

        // Parse --output-schema
        const parsedOutputSchema = parseSchemaOption(
          options.outputSchema, undefined,
          'output-schema', 'mutagent prompts create --help'
        );
        if (parsedOutputSchema) data.outputSchema = parsedOutputSchema;

        // Parse --input-schema
        const parsedInputSchema = parseSchemaOption(
          options.inputSchema, undefined,
          'input-schema', 'mutagent prompts create --help'
        );
        if (parsedInputSchema) data.inputSchema = parsedInputSchema;

        // outputSchema is REQUIRED — error if missing
        if (isSchemaEmpty(data.outputSchema)) {
          throw new MutagentError(
            'MISSING_ARGUMENTS',
            'outputSchema is required for prompt creation',
            'Run: mutagent prompts create --help\nUse --output-schema \'{"type":"object","properties":{...}}\''
          );
        }

        // Enforce inputSchema is present and valid (hard block)
        if (isSchemaEmpty(data.inputSchema)) {
          throw new MutagentError(
            'VALIDATION_ERROR',
            'inputSchema is required. Optimization cannot run without defined input variables.',
            'Run: mutagent prompts create --help\ninputSchema must be provided. Pass it via the API or dashboard.'
          );
        } else if (!isValidJsonSchema(data.inputSchema)) {
          throw new MutagentError(
            'VALIDATION_ERROR',
            'inputSchema is not a valid JSON Schema object.',
            'Run: mutagent prompts create --help\n' + formatSchemaWarning('inputSchema')
          );
        } else {
          const missingDescs = validateSchemaDescriptions(data.inputSchema);
          if (missingDescs.length > 0) {
            throw new MutagentError(
              'VALIDATION_ERROR',
              `inputSchema properties missing descriptions: ${missingDescs.join(', ')}`,
              "Run: mutagent prompts create --help\nAdd a 'description' field to each property in your inputSchema. Example: { \"properties\": { \"field\": { \"type\": \"string\", \"description\": \"What this field contains\" } } }"
            );
          }
        }

        // Warn about missing/invalid outputSchema (non-blocking)
        if (isSchemaEmpty(data.outputSchema)) {
          output.warn('No outputSchema provided. This may limit optimization effectiveness.');
        } else if (!isValidJsonSchema(data.outputSchema)) {
          output.warn(formatSchemaWarning('outputSchema'));
        }

        const client = await getSDKClient();
        const prompt = await client.createPrompt(data);

        if (isJson) {
          const directive = promptCreatedDirective(prompt.id, prompt.name);
          output.output({ ...prompt, _links: promptLinks(prompt.id), _directive: directive });
          echoDirectiveToStderr(directive);
        } else {
          output.success(`Created prompt: ${prompt.name} (id: ${String(prompt.id)})`);
          output.info(`Workspace: ${client.getCurrentWorkspaceId() ?? 'auto-inferred from API key'}`);
          const hints = formatCreationHints({
            resourceType: 'Prompt',
            id: prompt.id,
            name: prompt.name,
            dashboardUrl: promptLink(prompt.id),
            apiPath: `/api/prompts/${String(prompt.id)}`,
          });
          console.log(hints);
        }

      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Update prompt
  prompts
    .command('update')
    .description('Update a prompt')
    .argument('<id>', 'Prompt ID (from: mutagent prompts list)')
    .option('-n, --name <name>', 'New name')
    .option('--description <text>', 'New description (shown in dashboard)')
    .option('-c, --content <content>', 'New content (rawPrompt) [DEPRECATED: use --raw]')
    .option('-r, --raw <text>', 'Raw prompt text (single prompt)')
    .option('--system <text>', 'System prompt (use with --human)')
    .option('--human <text>', 'Human prompt (use with --system)')
    .option('--messages <json>', 'Messages array as JSON (e.g., \'[{"role":"system","content":"..."}]\')')
    .option('--input-schema <json>', 'Input schema as JSON string')
    .option('--input-schema-file <path>', 'Input schema from JSON file')
    .option('--output-schema <json>', 'Output schema as JSON string')
    .option('--output-schema-file <path>', 'Output schema from JSON file')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts update <id> --system "Updated system prompt" --human "{input}"
  ${chalk.dim('$')} mutagent prompts update <id> --name "new-name" --description "Updated description"
  ${chalk.dim('$')} mutagent prompts update <id> --raw "Summarize: {text}"
  ${chalk.dim('$')} mutagent prompts update <id> --messages '[{"role":"system","content":"Updated"},{"role":"user","content":"{input}"}]'
  ${chalk.dim('$')} mutagent prompts update <id> --input-schema '{"type":"object","properties":{"text":{"type":"string","description":"Input text"}}}'
    `)
    .action(async (id: string, options: UpdateOptions) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const data: Partial<Prompt> = {};

        if (options.name) data.name = options.name;
        if (options.description) data.description = options.description;

        // Handle different prompt style formats
        // Priority: messages > system+human > raw > content (deprecated)
        if (options.messages) {
          // Messages format: JSON array of messages
          try {
            const messages = JSON.parse(options.messages) as { role: string; content: string }[];
            // Extract system and human prompts from messages array
            const systemMsg = messages.find(m => m.role === 'system');
            const humanMsg = messages.find(m => m.role === 'user' || m.role === 'human');
            if (systemMsg) data.systemPrompt = systemMsg.content;
            if (humanMsg) data.humanPrompt = humanMsg.content;
          } catch {
            throw new MutagentError(
              'INVALID_JSON',
              'Invalid JSON in --messages flag',
              'Run: mutagent prompts update --help\n' +
              'Provide a valid JSON array, e.g., \'[{"role":"system","content":"..."}]\''
            );
          }
        } else if (options.system ?? options.human) {
          // Human/System format
          if (options.system) data.systemPrompt = options.system;
          if (options.human) data.humanPrompt = options.human;
        } else if (options.raw) {
          // Raw prompt format
          data.rawPrompt = options.raw;
        } else if (options.content) {
          // Deprecated --content flag (backwards compatibility)
          data.rawPrompt = options.content;
        }

        // Parse --input-schema / --input-schema-file
        const parsedInputSchema = parseSchemaOption(
          options.inputSchema, options.inputSchemaFile,
          'input-schema', 'mutagent prompts update --help'
        );
        if (parsedInputSchema) data.inputSchema = parsedInputSchema;

        // Parse --output-schema / --output-schema-file
        const parsedOutputSchema = parseSchemaOption(
          options.outputSchema, options.outputSchemaFile,
          'output-schema', 'mutagent prompts update --help'
        );
        if (parsedOutputSchema) data.outputSchema = parsedOutputSchema;

        // Validate inputSchema descriptions if provided
        if (data.inputSchema && isValidJsonSchema(data.inputSchema)) {
          const missingDescs = validateSchemaDescriptions(data.inputSchema);
          if (missingDescs.length > 0) {
            throw new MutagentError(
              'VALIDATION_ERROR',
              `inputSchema properties missing descriptions: ${missingDescs.join(', ')}`,
              'Run: mutagent prompts update --help\n' +
              "Add a 'description' field to each property in your inputSchema. Example: { \"properties\": { \"field\": { \"type\": \"string\", \"description\": \"What this field contains\" } } }"
            );
          }
        }

        if (Object.keys(data).length === 0) {
          throw new MutagentError(
            'MISSING_ARGUMENTS',
            'No update data provided',
            'Run: mutagent prompts update --help\n' +
            'Use --name, --raw, --system/--human, --messages, --input-schema, or --output-schema'
          );
        }

        const client = await getSDKClient();
        const prompt = await client.updatePrompt(id, data);

        if (isJson) {
          const directive = promptUpdatedDirective(prompt.id, prompt.name);
          output.output({ ...prompt, _links: promptLinks(prompt.id), _directive: directive });
          echoDirectiveToStderr(directive);
        } else {
          output.success(`Updated prompt: ${prompt.name} (id: ${String(prompt.id)})`);
          const hints = formatCreationHints({
            resourceType: 'Prompt',
            id: prompt.id,
            name: prompt.name,
            dashboardUrl: promptLink(prompt.id),
            apiPath: `/api/prompts/${String(prompt.id)}`,
          });
          console.log(hints);
        }

      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Delete prompt
  prompts
    .command('delete')
    .description('Delete a prompt')
    .argument('<id>', 'Prompt ID (from: mutagent prompts list)')
    .option('--force', 'Skip confirmation')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts delete <id>
  ${chalk.dim('$')} mutagent prompts delete <id> --force
  ${chalk.dim('$')} mutagent prompts delete <id> --force --json

${chalk.dim('Note: --force is required. The CLI is non-interactive — confirm with the user via your native flow, then pass --force.')}
    `)
    .action(async (id: string, options: DeleteOptions) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        // In --json mode, auto-skip confirmation. Otherwise require --force.
        if (!options.force && !isJson) {
          throw new MutagentError(
            'CONFIRMATION_REQUIRED',
            `Deleting prompt ${id} requires confirmation`,
            'Run: mutagent prompts delete --help\n' +
            `[Agent: Ask the user to confirm via MultiChoice, then pass --force]\n` +
            `Use --force to confirm: mutagent prompts delete ${id} --force`
          );
        }

        const client = await getSDKClient();
        try {
          await client.deletePrompt(id, { force: true });
        } catch (deleteError) {
          // 404 = already deleted = idempotent success
          if (deleteError instanceof ApiError && deleteError.statusCode === 404) {
            if (isJson) {
              const directive = promptDeletedDirective(id);
              output.output({ success: true, deletedId: id, _links: { prompts: promptsDashboardLink() }, _directive: directive });
              echoDirectiveToStderr(directive);
            } else {
              output.success(`Prompt ${id} already deleted (idempotent)`);
            }
            return;
          }
          throw deleteError;
        }

        if (isJson) {
          const directive = promptDeletedDirective(id);
          output.output({ success: true, deletedId: id, _links: { prompts: promptsDashboardLink() }, _directive: directive });
          echoDirectiveToStderr(directive);
        } else {
          output.success(`Deleted prompt: ${id}`);
          output.info(`View prompts: ${promptsDashboardLink()}`);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });
}
